import { CarrierAlert } from "./carrier-alert";
import { Checkbox } from "@material-ui/core";
import { OfferComission } from "./offer-comission";
import { OfferLogHistory } from "../offer-log-history";
import { OfferSourceSelect } from "../../search-field-selects/offer-sources-select";
import { Step } from "./step";
import {
  Button,
  Input,
  Modal,
  useDisclosure,
} from "../../../../../../../shared/infrastructure";
import {
  CarrierSelect,
  LocationSelect,
  OrderSelect,
} from "../../search-field-selects";
import {
  CarrierT,
  LocationT,
  OfferSourceT,
  OrderT,
} from "../../../../../domain";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { GeneralStepProps, OfferFormT } from "./types";
import { OrderDetails, isInvalidOrderDate } from "./order-details";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useLocationDistance } from "../../../../services";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const EmptyMilesComponent = (): React.ReactElement => {
  const { control } = useFormContext<OfferFormT>();

  const [order, truckLocation] = useWatch({
    control,
    name: ["order", "truckLocation"],
  });

  const [filterEmptyMiles, setFilterEmptyMiles] = useState<
    Array<string> | undefined
  >(undefined);
  const {
    data: emptyMiles,
    isLoading: isEmptyMilesIsLoading,
    isSuccess: isEmptyMilesSuccess,
  } = useLocationDistance(
    { coordinates: filterEmptyMiles as Array<string> },
    { enabled: !!filterEmptyMiles }
  );

  useEffect(() => {
    if (
      order?.loads_origin?.latitude &&
      order?.loads_origin?.longitude &&
      truckLocation?.latitude &&
      truckLocation?.longitude
    ) {
      setFilterEmptyMiles([
        `${order?.loads_origin?.latitude};${order?.loads_origin?.longitude}`,
        `${truckLocation?.latitude};${truckLocation?.longitude}`,
      ]);
    }
  }, [order, truckLocation]);

  return filterEmptyMiles?.length ? (
    <div className="mb-2 flex flex-row gap-[20px]">
      <div className="flex-1">
        <section className="grid grid-flow-col justify-between gap-[10px] rounded-[5px] bg-[#3C7CF338] px-[12px] py-[4px] text-[10px]">
          <span className="text-[#484848]">Empty Miles</span>
          <span className="font-[700] text-[#000000]">
            {isEmptyMilesSuccess && !isEmptyMilesIsLoading ? (
              emptyMiles?.data ? (
                `${emptyMiles.data} miles`
              ) : (
                "- -"
              )
            ) : (
              <AiOutlineLoading3Quarters className="m-0 h-4 w-4 animate-spin p-0" />
            )}
          </span>
        </section>
      </div>
      <div className="flex-1" />
    </div>
  ) : (
    <Fragment />
  );
};

const OfferFormActions = ({
  setStep,
}: GeneralStepProps): React.ReactElement => {
  const { isOpen, open, close } = useDisclosure();
  const {
    control,
    formState: { errors },
  } = useFormContext<OfferFormT>();
  const [carrier, order, truckLocation, rate, offerSource] = useWatch({
    control,
    name: ["carrier", "order", "truckLocation", "rate", "offerSource"],
  });

  const invalidOrderDate = useMemo(
    (): boolean =>
      isInvalidOrderDate(order?.loads_origin?.arrive_early_datetime),
    [order]
  );

  const couldContinue =
    carrier &&
    !carrier.no_dispatch &&
    order &&
    truckLocation &&
    rate &&
    offerSource &&
    !errors.offerSource &&
    !errors.carrier &&
    !errors.order &&
    !errors.truckLocation &&
    !errors.rate &&
    !invalidOrderDate;

  return (
    <section className="mt-[20px] grid grid-flow-col justify-center gap-[8px]">
      {!!carrier && (
        <Button
          className="w-auto px-[16px] py-[6px] text-[12px]"
          appearance="secondary"
          status={carrier ? "default" : "disabled"}
          onClick={open}
          type="button"
        >
          See Carrier’s Lane History
        </Button>
      )}
      <Button
        className={`w-auto px-[16px] py-[6px] text-[12px] ${
          couldContinue ? "bg-[#3C7CF3]" : "bg-[#9E9E9E]"
        }`}
        onClick={() => {
          setStep("summary");
        }}
        status={couldContinue ? "default" : "disabled"}
        type="submit"
      >
        Continue
      </Button>
      <Modal
        open={isOpen}
        onClose={close}
        content={
          carrier ? (
            <OfferLogHistory carrier={carrier} />
          ) : (
            <Fragment></Fragment>
          )
        }
        classNames={{
          content: "overflow-hidden p-[20px] w-[1100px] min-h-[360px]",
          paper: "rounded-[5px] min-w-[1100px] max-w-[100vw]",
        }}
      />
    </section>
  );
};

export const OfferFormStep = ({
  setStep,
}: GeneralStepProps): React.ReactElement => {
  const {
    control,
    setValue,
    formState: { defaultValues },
  } = useFormContext<OfferFormT>();

  return (
    <Step
      title="Rate Entry"
      subtitle="Please assign a carrier:"
      actions={<OfferFormActions setStep={setStep} />}
    >
      <section className="grid w-full justify-self-start">
        <div className="flex flex-row gap-[20px]">
          <div className="flex-1">
            <Controller
              name="carrier"
              control={control}
              render={({ field: { value, onChange }, fieldState }) => (
                <CarrierSelect
                  name="carrier"
                  keyProp="name"
                  placeholder="Carrier ID, DOT or MC#"
                  selectedValue={value as CarrierT}
                  onChangeValue={(newValue) => {
                    if (!Array.isArray(newValue)) {
                      onChange(newValue);
                    }
                  }}
                  error={fieldState?.error?.message ?? ""}
                  required
                />
              )}
            />
          </div>
          <div className="flex-1">
            <CarrierAlert />
          </div>
        </div>
        <div className="flex flex-row gap-[20px]">
          <div className="flex-1">
            <Controller
              name="order"
              control={control}
              render={({ field: { value, onChange }, fieldState }) => (
                <OrderSelect
                  name="order"
                  keyProp="orderNumber"
                  placeholder="Order Number"
                  disabled={!!defaultValues?.order}
                  selectedValue={value as OrderT}
                  onChangeValue={(newValue) => {
                    if (!Array.isArray(newValue)) {
                      onChange(newValue);
                      setValue(
                        "origin_zip_code",
                        newValue?.loads_origin?.zipCode
                          ? Number(newValue?.loads_origin?.zipCode)
                          : undefined
                      );
                      setValue(
                        "destination_zip_code",
                        newValue?.loads_destination?.zipCode
                          ? Number(newValue?.loads_destination?.zipCode)
                          : undefined
                      );
                      setValue("rate", undefined);
                      setValue("truckLocation", undefined);
                      setValue("emptyTruck", false);
                    }
                  }}
                  error={fieldState?.error?.message ?? ""}
                  required
                />
              )}
            />
          </div>
          <div className="flex-1">
            <OrderDetails />
          </div>
        </div>
        <div className="flex flex-row gap-[20px]">
          <div className="flex-1">
            <Controller
              name="truckLocation"
              control={control}
              render={({ field: { value, onChange }, fieldState }) => (
                <LocationSelect
                  name="truck-location"
                  keyProp="city"
                  selectedValue={value as LocationT}
                  onChangeValue={(newValue) => {
                    if (!Array.isArray(newValue)) {
                      onChange(newValue);
                    }
                  }}
                  placeholder="Current Truck Location"
                  error={fieldState?.error?.message ?? ""}
                  required
                />
              )}
            />
          </div>
          <div className="flex-1">
            <Controller
              name="emptyTruck"
              control={control}
              render={({ field: { value, onChange } }) => (
                <div className="gap flex flex-row">
                  <Checkbox
                    checked={(value as boolean) ?? false}
                    value={(value as boolean) ?? false}
                    onChange={() => onChange(!value)}
                    edge="start"
                    size="small"
                    tabIndex={-1}
                    disableRipple
                    className="pl-0 text-blue-500 hover:bg-transparent"
                  />
                  <span className="flex items-center text-[14px]">
                    Empty Truck
                  </span>
                </div>
              )}
            />
          </div>
        </div>

        <EmptyMilesComponent />

        <div className="flex flex-row gap-[20px]">
          <div className="flex-1">
            <Controller
              name="offerSource"
              control={control}
              render={({ field: { value, onChange }, fieldState }) => (
                <OfferSourceSelect
                  name="offer-source"
                  keyProp="id"
                  selectedValue={value as OfferSourceT}
                  onChangeValue={(newValue) => {
                    if (!Array.isArray(newValue)) {
                      onChange(newValue);
                    }
                  }}
                  placeholder="Offer Source"
                  error={fieldState?.error?.message ?? ""}
                  required
                />
              )}
            />
          </div>
          <div className="flex-1"></div>
        </div>
        <p className="mb-5 text-[14px]">Quote</p>
        <div className="flex flex-row gap-[20px]">
          <div className="flex-1">
            <Input
              classNames={{ formControl: "w-full" }}
              rootInputProps={{ className: "px-4" }}
              name="rate"
              placeholder="Rate"
              required
              fullWidth={true}
            />
          </div>
          <div className="flex-1">
            <OfferComission />
          </div>
        </div>
      </section>
      {/* <section className="grid grid-flow-col justify-start gap-[10px] text-[14px]">
        <span>Max buy:</span>
        <span className="text-[#4CAF50]">
          {data.order?.max_buy
            ? `${data.order.max_buy.toLocaleString("en-US")} USD`
            : "- -"}
        </span>
      </section> */}
    </Step>
  );
};
